import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Disclosure, Menu, Transition } from '@headlessui/react'
import { 
  Bars3Icon, 
  XMarkIcon, 
  UserCircleIcon,
  BellIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../../contexts/AuthContext'
import ImageWithFallback from '../ui/ImageWithFallback'
import { getLocalImage } from '../../utils/images'
import { Button } from '../ui/button'
import clsx from 'clsx'

const navigation = [
  { name: 'Home', href: '/', description: 'Welcome to ZovaLabs' },
  { name: 'About', href: '/about', description: 'Learn about our mission' },
  { name: 'Challenges', href: '/challenges', description: 'Explore AI challenges' },
  { name: 'Leaderboard', href: '/leaderboard', description: 'See top performers' },
]

const userNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: UserCircleIcon },
  { name: 'Profile', href: '/profile', icon: UserCircleIcon },
  { name: 'My Submissions', href: '/submissions', icon: Cog6ToothIcon },
]

const adminNavigation = [
  { name: 'Admin Dashboard', href: '/admin/dashboard', icon: ShieldCheckIcon },
  { name: 'Manage Challenges', href: '/admin/challenges', icon: ShieldCheckIcon },
  { name: 'Review Submissions', href: '/admin/submissions', icon: ShieldCheckIcon },
  { name: 'Manage Users', href: '/admin/users', icon: ShieldCheckIcon },
]

export default function Navbar() {
  const { user, isAuthenticated, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10
      setScrolled(isScrolled)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  return (
    <Disclosure as="nav" className={clsx(
      "sticky top-0 z-50 transition-all duration-300",
      scrolled 
        ? "glass-morphism shadow-2xl border-b border-gold-500/30" 
        : "bg-dark-900/90 backdrop-blur-sm shadow-lg border-b border-dark-800"
    )}>
      {({ open }) => (
        <>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-18">
              {/* Logo - Left Side */}
              <div className="flex-shrink-0 flex items-center">
                <Link to="/" className="flex items-center group">
                  <ImageWithFallback
                    src="/images/zova_labs_right.png"
                    alt="ZovaLabs Logo"
                    className="w-32 h-32 object-contain transition-all duration-300 group-hover:scale-105 -my-6"
                  />
                </Link>
              </div>

              {/* Navigation - Center */}
              <div className="hidden sm:flex sm:space-x-2 absolute left-1/2 transform -translate-x-1/2">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={clsx(
                      'relative inline-flex items-center px-6 py-3 text-sm font-display font-medium rounded-xl transition-all duration-300 group',
                      location.pathname === item.href
                        ? 'text-gold-400 glass-morphism border border-gold-500/30'
                        : 'text-gray-300 hover:text-gold-400 hover:glass-morphism'
                    )}
                  >
                    <span className="relative z-10">{item.name}</span>
                    {location.pathname === item.href && (
                      <>
                        <div className="absolute inset-0 bg-gradient-gold opacity-10 rounded-xl"></div>
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-gradient-gold animate-gold-shimmer"></div>
                      </>
                    )}
                    <div className="absolute inset-0 rounded-xl bg-gradient-gold opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                    <div className="absolute top-1 right-1 w-1 h-1 bg-gold-400 rounded-full opacity-0 group-hover:opacity-100 animate-sparkle transition-opacity duration-300"></div>
                  </Link>
                ))}
              </div>

              {/* User Menu - Right Side */}
              <div className="hidden sm:ml-6 sm:flex sm:items-center sm:space-x-4">
                {isAuthenticated ? (
                  <>
                    {/* Notifications */}
                    <button className="relative p-2 text-gray-400 hover:text-primary-400 hover:bg-dark-800 rounded-lg transition-colors duration-200">
                      <span className="sr-only">View notifications</span>
                      <BellIcon className="h-5 w-5" />
                      <div className="absolute top-1 right-1 w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
                    </button>

                    {/* User Menu */}
                    <Menu as="div" className="relative">
                      <div>
                        <Menu.Button className="flex items-center space-x-3 text-sm rounded-lg p-2 hover:bg-dark-800 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark-900 focus:ring-primary-500">
                          <ImageWithFallback
                            src={getLocalImage('experts')}
                            alt={user?.firstName}
                            className="h-8 w-8 rounded-full object-cover ring-2 ring-primary-500/50"
                            fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' /%3E%3C/svg%3E"
                          />
                          <div className="hidden lg:flex lg:flex-col lg:items-start">
                            <span className="text-sm font-medium text-gray-100">{user?.firstName}</span>
                            <span className="text-xs text-gray-400">View profile</span>
                          </div>
                          <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                        </Menu.Button>
                      </div>
                      <Transition
                        enter="transition ease-out duration-200"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="origin-top-right absolute right-0 mt-3 w-64 rounded-xl shadow-xl bg-dark-800 ring-1 ring-primary-500/20 focus:outline-none overflow-hidden border border-dark-700">
                          {/* User Info Header */}
                          <div className="px-4 py-4 bg-gradient-to-r from-primary-500/10 to-primary-600/10 border-b border-dark-700">
                            <div className="flex items-center space-x-3">
                              <ImageWithFallback
                                src={getLocalImage('experts')}
                                alt={user?.firstName}
                                className="h-10 w-10 rounded-full object-cover ring-2 ring-primary-500/50"
                                fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' /%3E%3C/svg%3E"
                              />
                              <div>
                                <div className="font-display font-semibold text-gray-100">{user?.firstName} {user?.lastName}</div>
                                <div className="text-sm text-gray-400">{user?.email}</div>
                              </div>
                            </div>
                          </div>

                          {/* Navigation Items */}
                          <div className="py-2">
                            {userNavigation.map((item) => {
                              const Icon = item.icon
                              return (
                                <Menu.Item key={item.name}>
                                  {({ active }) => (
                                    <Link
                                      to={item.href}
                                      className={clsx(
                                        'flex items-center px-4 py-3 text-sm font-medium transition-colors duration-200',
                                        active 
                                          ? 'bg-primary-500/10 text-primary-400' 
                                          : 'text-gray-300 hover:bg-dark-700 hover:text-primary-400'
                                      )}
                                    >
                                      <Icon className="h-5 w-5 mr-3 text-gray-400" />
                                      {item.name}
                                    </Link>
                                  )}
                                </Menu.Item>
                              )
                            })}
                          </div>

                          {/* Admin Navigation */}
                          {user?.role === 'admin' && (
                            <>
                              <div className="border-t border-dark-700 pt-2">
                                <div className="px-4 py-2">
                                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                    Admin
                                  </p>
                                </div>
                                {adminNavigation.map((item) => {
                                  const Icon = item.icon
                                  return (
                                    <Menu.Item key={item.name}>
                                      {({ active }) => (
                                        <Link
                                          to={item.href}
                                          className={clsx(
                                            'flex items-center px-4 py-3 text-sm font-medium transition-colors duration-200',
                                            active 
                                              ? 'bg-purple-500/10 text-purple-400' 
                                              : 'text-gray-300 hover:bg-dark-700 hover:text-purple-400'
                                          )}
                                        >
                                          <Icon className="h-5 w-5 mr-3 text-purple-400" />
                                          {item.name}
                                        </Link>
                                      )}
                                    </Menu.Item>
                                  )
                                })}
                              </div>
                            </>
                          )}

                          {/* Logout */}
                          <div className="border-t border-dark-700">
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={handleLogout}
                                  className={clsx(
                                    'flex items-center w-full px-4 py-3 text-sm font-medium transition-colors duration-200',
                                    active 
                                      ? 'bg-red-500/10 text-red-400' 
                                      : 'text-gray-300 hover:bg-dark-700 hover:text-red-400'
                                  )}
                                >
                                  <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 text-gray-400" />
                                  Sign out
                                </button>
                              )}
                            </Menu.Item>
                          </div>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </>
                ) : (
                  <div className="flex items-center space-x-4">
                    <Button variant="ghost" asChild>
                      <Link to="/login">Sign in</Link>
                    </Button>
                    <Button variant="gold" asChild>
                      <Link to="/register">Sign up</Link>
                    </Button>
                  </div>
                )}
              </div>
              <div className="-mr-2 flex items-center sm:hidden">
                <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-primary-400 hover:bg-dark-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-colors duration-200 gold-border-animated">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          <Disclosure.Panel className="sm:hidden bg-dark-900 border-t border-dark-800">
            <div className="pt-4 pb-3 space-y-1 px-4">
              {navigation.map((item) => (
                <Disclosure.Button
                  key={item.name}
                  as={Link}
                  to={item.href}
                  className={clsx(
                    'block px-4 py-3 rounded-lg text-base font-display font-medium transition-colors duration-200',
                    location.pathname === item.href
                      ? 'bg-primary-500/10 text-primary-400 gold-border'
                      : 'text-gray-300 hover:bg-dark-800 hover:text-primary-400'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span>{item.name}</span>
                    {location.pathname === item.href && (
                      <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">{item.description}</div>
                </Disclosure.Button>
              ))}
            </div>
            {isAuthenticated ? (
              <div className="pt-4 pb-4 border-t border-dark-800 mx-4">
                <div className="flex items-center px-4 py-3 bg-gradient-to-r from-primary-500/10 to-primary-600/10 rounded-lg border border-primary-500/20">
                  <div className="flex-shrink-0">
                    <ImageWithFallback
                      src={getLocalImage('experts')}
                      alt={user?.firstName}
                      className="h-12 w-12 rounded-full object-cover ring-2 ring-primary-500/50"
                      fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' /%3E%3C/svg%3E"
                    />
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-display font-semibold text-gray-100">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className="text-sm text-gray-400">{user?.email}</div>
                  </div>
                </div>
                <div className="mt-4 space-y-1">
                  {userNavigation.map((item) => {
                    const Icon = item.icon
                    return (
                      <Disclosure.Button
                        key={item.name}
                        as={Link}
                        to={item.href}
                        className="flex items-center px-4 py-3 text-base font-medium text-gray-300 hover:text-primary-400 hover:bg-dark-800 rounded-lg transition-colors duration-200"
                      >
                        <Icon className="h-5 w-5 mr-3 text-gray-400" />
                        {item.name}
                      </Disclosure.Button>
                    )
                  })}
                  
                  {/* Admin Navigation for Mobile */}
                  {user?.role === 'admin' && (
                    <>
                      <div className="pt-4 pb-2">
                        <div className="px-4">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            Admin
                          </p>
                        </div>
                      </div>
                      {adminNavigation.map((item) => {
                        const Icon = item.icon
                        return (
                          <Disclosure.Button
                            key={item.name}
                            as={Link}
                            to={item.href}
                            className="flex items-center px-4 py-3 text-base font-medium text-gray-300 hover:text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors duration-200"
                          >
                            <Icon className="h-5 w-5 mr-3 text-purple-400" />
                            {item.name}
                          </Disclosure.Button>
                        )
                      })}
                    </>
                  )}
                  
                  <Disclosure.Button
                    as="button"
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-3 text-base font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors duration-200"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                    Sign out
                  </Disclosure.Button>
                </div>
              </div>
            ) : (
              <div className="pt-4 pb-4 border-t border-dark-800 mx-4">
                <div className="space-y-3">
                  <Button variant="ghost" asChild className="w-full">
                    <Link to="/login">Sign in</Link>
                  </Button>
                  <Button variant="gold" asChild className="w-full">
                    <Link to="/register">Sign up</Link>
                  </Button>
                </div>
              </div>
            )}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  )
}